import { DropdownAutoCompleteSelect, OptionType } from "components/form-control/dropdown-autocomplete";
import { FormControl, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { useEffect, useState } from "react";

import { CityInput } from "types";
import { Input } from "@/components/ui/input";
import WorldCities from "worldcities";
import { useDebounce } from "hooks/lib-ui";
import { useFormContext } from "react-hook-form";

interface GeneralProps {
  handleSearch: (value: string) => void;
  isLoading: boolean;
  result: OptionType[];
  cityName: string;
  handleSelectCity: (value: string, label: string) => void;
}

export const GeneralCityDetails = ({ isLoading, result, cityName, handleSelectCity, handleSearch }: GeneralProps) => {
  const { control, setValue, getValues } = useFormContext<CityInput>();

  const timezoneByCity = getValues("timezone");
  const [searchValue, setSearchValue] = useState<string>("");
  const searchString = useDebounce(searchValue);
  const [listTimeZones, setListTimeZones] = useState<OptionType[]>([]);

  useEffect(() => {
    setValue("name", cityName);
  }, [cityName, setValue]);

  useEffect(() => {
    if (timezoneByCity) {
      setListTimeZones([{ label: timezoneByCity, value: timezoneByCity }]);
      setSearchValue("");
    }
  }, [timezoneByCity]);

  useEffect(() => {
    if (searchString.length >= 3) {
      const cities = WorldCities.getAllByName(searchString);
      setListTimeZones(
        cities.map((item) => ({ label: item.timezone, value: item.timezone, description: item.name })) as OptionType[],
      );
    } else if (listTimeZones.length) {
      setListTimeZones([]);
    }
  }, [searchString, listTimeZones.length]);

  return (
    <div className="px-4 pt-4 pb-2 text-sm space-y-6">
      <div className="grid grid-cols-2 gap-4 items-start">
        <FormField
          control={control}
          name="name"
          rules={{
            required: "Le nom de la ville est requis",
          }}
          render={() => (
            <FormItem>
              <FormLabel>
                Nom de la ville<span className="text-red-500">*</span>
              </FormLabel>
              <FormControl>
                <DropdownAutoCompleteSelect
                  value={cityName}
                  onChangeValue={handleSelectCity}
                  handleSearch={handleSearch}
                  displayValue={cityName}
                  searchString={cityName}
                  options={result}
                  isLoading={isLoading}
                  name="name"
                  className="w-[285px]"
                  placeholder="Rechercher une ville..."
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        <FormField
          control={control}
          name="zipCode"
          rules={{
            required: "Le code postal est requis",
          }}
          render={({ field }) => (
            <FormItem>
              <FormLabel>
                Code postal<span className="text-red-500">*</span>
              </FormLabel>
              <FormControl>
                <Input {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
      </div>

      <div className="grid grid-cols-2 gap-4">
        <FormField
          control={control}
          name="departement"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Département</FormLabel>
              <FormControl>
                <Input {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        <FormField
          control={control}
          name="state"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Région</FormLabel>
              <FormControl>
                <Input {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        <FormField
          control={control}
          name="country"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Pays</FormLabel>
              <FormControl>
                <Input {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        <FormField
          control={control}
          name="countryCode"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Code pays</FormLabel>
              <FormControl>
                <Input {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        <FormField
          control={control}
          name="surfaceArea"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Superficie</FormLabel>
              <FormControl>
                <Input {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
      </div>

      <div>
        <h1 className="font-bold text-sm mb-2">Devise</h1>
        <div className="grid grid-cols-2 gap-4">
          <FormField
            control={control}
            name="currency.code"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Code</FormLabel>
                <FormControl>
                  <Input {...field} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          <FormField
            control={control}
            name="currency.symbol"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Symbole</FormLabel>
                <FormControl>
                  <Input {...field} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
        </div>
      </div>

      <FormField
        control={control}
        name="timezone"
        render={({ field }) => (
          <FormItem>
            <FormLabel>Fuseau horaire</FormLabel>
            <FormControl>
              <DropdownAutoCompleteSelect
                value={field.value}
                options={listTimeZones}
                onChangeValue={field.onChange}
                handleSearch={setSearchValue}
                searchString={searchValue}
                className="w-full"
              />
            </FormControl>
            <FormMessage />
          </FormItem>
        )}
      />
    </div>
  );
};
